<template>
  <b-card
    body-class="border"
    class="border-0"
  >
    <b-form-group>
      <label>Links to</label>
      <div class="position-relative tiptap-link">
        <pages-select-menu
          :others="[
            {
              id: 'custom-link',
              title: 'Custom Link'
            },
            {
              id: 'cookie-consent',
              title: 'Cookie Consent',
              slug: 'cookie-consent'
            },
            {
              id: 'download-file',
              title: 'Download File'
            },
            {
              id: 'blog',
              title: 'ESTAGE Blog',
              customLink: `/${project.blog_slug}`,
              target: '_self',
              code: project.blog_slug,
              slug: 'blog',
              href: '/blog'
            }
          ]"
          input-class="rounded-[4px] !h-[28px]"
          menu-class="!mt-[-15px]"
          other-title="Other:"
          title="Pages and Posts"
          add-posts
          @selectOther="onSelect"
          @selectPage="onSelect"
        >
          <template
            v-if="form.page"
            slot="placeholder"
          >
            <div class="px-3">
              {{ form.page.title || 'Select Link' }}
            </div>
          </template>
        </pages-select-menu>
      </div>
    </b-form-group>

    <b-form-group v-if="form.page.id === 'custom-link'">
      <label>Custom link</label>
      <b-input
        v-model="form.customLink"
        placeholder="https://"
        type="url"
      />
    </b-form-group>

    <b-form-group v-if="form.page.id === 'download-file'">
      <UploadAsset
        title="Select File"
        type=".jpg,.jpeg,.png,.gif,.svg,.webp,.avif,.ico,.json,.pdf"
        :filename="form?.file?.name"
        @uploaded="uploaded"
      />
    </b-form-group>

    <b-form-group v-if="!['cookie-consent', 'download-file'].includes(form.page.id)">
      <b-checkbox
        v-model="form.target"
        unchecked-value="_self"
        value="_blank"
      >
        Open in new tab
      </b-checkbox>
    </b-form-group>

    <rule-tabs v-if="!hideStyles">
      <template #Normal>
        <div class="flex items-center justify-between mb-3">
          <label class="p-0">Link Style</label>
          <sid-font-style
            :sid="`${sid} a#${id}`"
            rule-name="font"
          />
        </div>

        <div class="flex items-center justify-between">
          <label class="p-0">Link Color</label>
          <sid-color
            :sid="`${sid} a#${id}`"
            rule-name="color"
          />
        </div>
      </template>
      <template #Hover>
        <div class="flex items-center justify-between mb-3">
          <label class="p-0">Link Style</label>
          <sid-font-style
            :sid="`${sid} a#${id}`"
            rule-name="font"
            presudo=":hover"
            :pseudo-index="1"
          />
        </div>

        <div class="flex items-center justify-between">
          <label class="p-0">Link Color</label>
          <sid-color
            :sid="`${sid} a#${id}`"
            presudo=":hover"
            rule-name="color"
            :pseudo-index="1"
          />
        </div>
      </template>
    </rule-tabs>

    <b-form-group v-if="!hideStyles">
      <b-checkbox
        v-model="form.fromGlobalStyle"
      >
        Global Style
      </b-checkbox>
    </b-form-group>
    
    <slot />
  </b-card>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import PagesPaginationMixin from '../mixins/PagesPaginationMixin'
import PagesSelectMenu from '@/components/editor/components/pagesSelectMenu/PagesSelectMenu'
import RuleTabs from '@/components/editor/components/RuleTabs'
import SidFontStyle from '@/components/editor/components/sid-controls/SidFontStyle'
import SidColor from '@/components/editor/components/sid-controls/SidColor'
import shortid from 'shortid'
import UploadAsset
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-image/UploadAsset.vue'

export default {
  name: 'TiptapLink',
  components: {
    SidColor, 
    SidFontStyle, 
    RuleTabs, 
    PagesSelectMenu,
    UploadAsset
  },
  mixins: [PagesPaginationMixin],

  props: {
    value: {
      type: Object,
      default: () => {
      }
    },
    sid: {
      type: String
    },
    hideStyles: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      target: true,
      id: null,
      form: {
        page: this.value.href,
        customLink: this.value.href.href,
        target: this.value.target,
        fromGlobalStyle: false,
        file: {
          name: null,
          url: null
        },
        style: {
          hover: {
            color: '',
            bold: false,
            italic: false,
            underline: false
          },
          normal: {
            color: '',
            bold: false,
            italic: false,
            underline: false
          }
        }
      }
    }
  },

  computed: {
    ...mapGetters('pages', {
      pages: 'getPages'
    }),
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapState('editor', {
      globalStyles: state => state.globalStyles
    })
  },

  watch: {
    value: {
      deep: true,
      handler: function (val) {
        const style = val.styleeffect ? JSON.parse(val.styleeffect) : {
          hover: {
            color: '',
            bold: false,
            italic: false,
            underline: false
          },
          normal: {
            color: '',
            bold: false,
            italic: false,
            underline: false
          }
        }

        this.id = val.id || `link-${shortid.generate()}`
        this.form = {
          page: val.href,
          customLink: val.href.href,
          target: val.target,
          file: val.file,
          fromGlobalStyle: val.fromGlobalStyle,
          style
        }
      }
    },
    form: {
      deep: true,
      handler: function () {

        let link = {
          custom_link: this.form.customLink,
          target: this.form.target,
          fromGlobalStyle: this.form.fromGlobalStyle
        }

        if (this.form.page !== 'custom-link') {
          link.href = this.form.page
        }

        if (!this.form.fromGlobalStyle) {
          link.styleeffect = JSON.stringify(this.form.style)
        } else {
          this.$set(link, 'styleeffect', null)
        }

        if (this.form.file) {
          this.$set(link, 'file', this.form.file)
        }

        link.href.href = this.form.customLink
        link.id = this.id

        this.$emit('change', link)
      }
    }
  },

  created () {
    this.id = `link-${shortid.generate()}`
    const style = this.value.styleeffect ? JSON.parse(this.value.styleeffect) : {
      hover: {
        color: '',
        bold: false,
        italic: false,
        underline: false
      },
      normal: {
        color: '',
        bold: false,
        italic: false,
        underline: false
      }
    }

    this.form.style = style
    this.form.fromGlobalStyle = this.value.fromGlobalStyle
  },

  methods: {
    onSelect (item) {
      this.form.page = {
        ...item,
        slug: item.slug ? `/${item.slug}` : null
      }
    },
    onClose () {
      this.searching = false
      const $menu = document.querySelector('.vs__dropdown-menu')

      if ($menu) {
        $menu.removeEventListener('scroll', this.scrollLoadingPages)
      }
    },
    uploaded (file) {
      this.form.customLink = file.url
      this.form.file = {
        name: file.original_name,
        url: file.url
      }
      this.form.target = '_blank'
    }
  }
}
</script>

<style>
.tiptap-link .pages-select-menu-dropdwon {
  width: 195px !important;
  min-width: 195px !important;
}
</style>
